import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { Edit, Delete, Add, Person, KeyOff, Warning } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getDriversByTransportLine, createDriver, updateDriver, deleteDriver } from '../../api';
import ConfirmDialog from '../ConfirmDialog';
import TransportistasService from '../../services/TransportistasService';

const DriversManagement = ({ selectedLine, onDataChange }) => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [driverForm, setDriverForm] = useState({
    nombre: '',
    apellido_paterno: '',
    apellido_materno: '',
    RFC: '',
    email: '',
    NSS: '',
    telefono: '',
    licencia: ''
  });

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDialogResetPass, setShowDialogResetPass] = useState(false);

  useEffect(() => {
    if (selectedLine) {
      fetchDrivers();
    }
  }, [selectedLine]);

  const fetchDrivers = async () => {
    if (!selectedLine) return;
    
    setLoading(true);
    try {
      const data = await getDriversByTransportLine(selectedLine.id);
      setDrivers(data);

      // Notificar al componente padre sobre cambios en los datos
      if (onDataChange) {
        onDataChange(selectedLine);
      }
    } catch (error) {
      toast.error('Error al cargar los conductores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (driver = null) => {
    if (driver) {
      setEditingDriver(driver);
      setDriverForm({
        nombre: driver.nombre || '',
        apellido_paterno: driver.apellido_paterno || '',
        apellido_materno: driver.apellido_materno || '',
        RFC: driver.RFC || '',
        email: driver.email || '',
        NSS: driver.NSS || '',
        telefono: driver.telefono || '',
        licencia: driver.licencia || ''
      });
    } else {
      setEditingDriver(null);
      setDriverForm({
        nombre: '',
        apellido_paterno: '',
        apellido_materno: '',
        RFC: '',
        email: '',
        NSS: '',
        telefono: '',
        licencia: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDriverForm({
      ...driverForm,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    if (!driverForm.nombre || !driverForm.apellido_paterno || !driverForm.RFC || !driverForm.licencia) {
      toast.error('Por favor complete los campos requeridos');
      return;
    }

    try {
      if (editingDriver) {
        await updateDriver(editingDriver.id, {
          ...driverForm,
          linea_transporte_id: selectedLine.id
        });
        toast.success('Conductor actualizado correctamente');
      } else {
        await createDriver({
          ...driverForm,
          linea_transporte_id: selectedLine.id
        });
        toast.success('Conductor creado correctamente');
      }
      
      handleCloseDialog();
      fetchDrivers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al procesar la solicitud';
      toast.error(errorMessage);
    }
  };
  
  const handleDeleteClick = (driver) => {
    setDriverToDelete(driver);
    setDeleteConfirmOpen(true);
  };
  
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDriverToDelete(null);
  };

  const handleDeleteDriver = async () => {
    if (!driverToDelete) return;
    
    try {
      await deleteDriver(driverToDelete.id);
      toast.success('Conductor eliminado correctamente');
      fetchDrivers();
      handleCloseDeleteConfirm();
    } catch (error) {
      toast.error('Error al eliminar el conductor');
    }
  };

  const closeDialogResetPass = () => {
    setShowDialogResetPass(false);
    setSelectedDriver(null);
  };

  const confirmDialogResetPass = async () => {
    console.log("enviar");

    try {
      let operadorData = {
        "id_operador": selectedDriver.id
      }

      const response = await TransportistasService.postResetPasswordOperador(operadorData)
      if (response.data && response.data.status == 200) {
        toast.success('Contraseña reiniciada correctamente.');

        setShowDialogResetPass(false);
        setSelectedDriver(null);
      } else {
        toast.error("Hubo un error en el servidor.");
      }

    } catch (error) {
      console.log(error);
      const mensajeError = error?.response?.data?.msg || 'Hubo un error en el servidor.';
      toast.error(mensajeError);
    }


  };

  const openDialogResetPass = (drive) => {
    console.log(drive);
    setShowDialogResetPass(true);
    setSelectedDriver(drive);
  };

  // Función para verificar si un conductor tiene datos incompletos
  const isDriverIncomplete = (driver) => {
    return !driver.nombre || 
           !driver.apellido_paterno || 
           !driver.RFC || 
           !driver.licencia || 
           !driver.telefono;
  };
  
  // Función para obtener los campos faltantes específicos de un conductor
  const getMissingFields = (driver) => {
    const missing = [];
    if (!driver.nombre) missing.push('Nombre');
    if (!driver.apellido_paterno) missing.push('Apellido Paterno');
    if (!driver.RFC) missing.push('RFC');
    if (!driver.licencia) missing.push('Licencia');
    if (!driver.telefono) missing.push('Teléfono');
    return missing;
  };

  if (!selectedLine) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6">
          Seleccione una línea de transporte para gestionar sus conductores
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Conductores de {selectedLine.name}
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={selectedLine.blocked}
        >
          Nuevo Conductor
        </Button>
      </Box>

      {drivers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No hay conductores registrados para esta línea de transporte
          </Typography>
        </Paper>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre Completo</TableCell>
                <TableCell>RFC</TableCell>
                <TableCell>Licencia</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.map((driver) => {
                const incomplete = isDriverIncomplete(driver);
                const missingFields = getMissingFields(driver);
                
                return (
                  <TableRow 
                    key={driver.id} 
                    sx={{
                      backgroundColor: incomplete ? '#fff4e5' : 'inherit',  // Sombreado naranja claro para filas incompletas
                      '&:hover': {
                        backgroundColor: incomplete ? '#ffe0b2' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {incomplete && (
                          <Tooltip title={`Datos incompletos: ${missingFields.join(', ')}`}>
                            <Warning color="warning" sx={{ mr: 1 }} />
                          </Tooltip>
                        )}
                        {`${driver.nombre || '(Sin nombre)'} ${driver.apellido_paterno || ''} ${driver.apellido_materno || ''}`}
                      </Box>
                    </TableCell>
                    <TableCell>{driver.RFC || '(Falta RFC)'}</TableCell>
                    <TableCell>{driver.licencia || '(Falta licencia)'}</TableCell>
                    <TableCell>{driver.telefono || '(Falta teléfono)'}</TableCell>
                    <TableCell>{driver.email || 'No disponible'}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleOpenDialog(driver)}
                        disabled={selectedLine.blocked}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(driver)}
                        disabled={selectedLine.blocked}
                      >
                        <Delete />
                      </IconButton>

                      <IconButton
                        onClick={() => openDialogResetPass(driver)}
                      >
                        <KeyOff />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para crear/editar conductor */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDriver ? 'Editar Conductor' : 'Nuevo Conductor'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nombre"
                name="nombre"
                value={driverForm.nombre}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Apellido Paterno"
                name="apellido_paterno"
                value={driverForm.apellido_paterno}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Apellido Materno"
                name="apellido_materno"
                value={driverForm.apellido_materno}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="RFC"
                name="RFC"
                value={driverForm.RFC}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Licencia"
                name="licencia"
                value={driverForm.licencia}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="NSS"
                name="NSS"
                value={driverForm.NSS}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                value={driverForm.telefono}
                onChange={handleInputChange}
                type="tel"
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={driverForm.email}
                onChange={handleInputChange}
                type="email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingDriver ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Confirmar eliminación"
        content={`¿Está seguro de que desea eliminar al conductor ${driverToDelete?.nombre} ${driverToDelete?.apellido_paterno}?`}
        onConfirm={handleDeleteDriver}
        onClose={handleCloseDeleteConfirm}
      />

      {/* Dialog para resetear contraseña */}
      <Dialog
        open={showDialogResetPass}
        onClose={closeDialogResetPass}
      >
        <DialogTitle>Restablecer contraseña</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Está seguro de que desea restablecer la contraseña del conductor {selectedDriver?.nombre} {selectedDriver?.apellido_paterno}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogResetPass}>Cancelar</Button>
          <Button onClick={confirmDialogResetPass} color="error" variant="contained">
            Restablecer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriversManagement;
